import { Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { flexRender, getCoreRowModel, getSortedRowModel, Row, useReactTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useRef } from 'react';

type Props<DATA, COLUMN> = {
  isLoading: boolean;
  data: Array<DATA>;
  columns: Array<COLUMN>;
  onRowClick?: (row: DATA) => void;
};

// eslint-disable-next-line func-style
export function VirtualTable<D, T>({ isLoading, data = [], columns, onRowClick }: Props<D, T>) {
  const table = useReactTable({
    data,
    columns: columns as any,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  const { rows } = table.getRowModel();

  // The virtualizer needs to know the scrollable container element
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    // estimate row height for accurate scrollbar dragging
    estimateSize: () => 100,
    getScrollElement: () => tableContainerRef.current,
    // measure dynamic row height, except in firefox because it measures table border height incorrectly
    measureElement:
      typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });

  return (
    <TableContainer ref={tableContainerRef} w="full" maxW="full" h="full" overflowY="auto">
      <Table position="relative" h="fit-content">
        <Thead position="sticky" top={0} bg="bg.subtle" zIndex="1">
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const meta: any = header.column.columnDef.meta;
                return (
                  <Th key={header.id} isNumeric={meta?.isNumeric} w={header.getSize()} boxSizing="content-box">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Show if={isLoading}>
          {/* Without Tbody+Tr+Td, react will complain about improper nesting of a div directly in a table. */}
          <Tbody>
            <Tr>
              <Td colSpan={columns.length}>
                <Text fontSize="sm">Loading...</Text>
              </Td>
            </Tr>
          </Tbody>
        </Show>
        <Show if={!isLoading && !rowVirtualizer.getVirtualItems().length}>
          <Tbody>
            <Tr>
              <Td colSpan={columns.length}>
                <Text fontSize="sm">No results. Double check filters.</Text>
              </Td>
            </Tr>
          </Tbody>
        </Show>
        <Show if={!isLoading}>
          <Tbody
            maxH="full"
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              position: 'relative', // needed for absolute positioning of rows
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index] as Row<any>;
              return (
                <Tr
                  data-index={virtualRow.index} //needed for dynamic row height measurement
                  ref={(node) => rowVirtualizer.measureElement(node)} //measure dynamic row height
                  key={row.id}
                  style={{
                    position: 'absolute',
                    transform: `translateY(${virtualRow.start}px)`, // this should always be a `style` as it changes on scroll
                  }}
                  cursor="pointer"
                  _hover={{ bg: 'bg.muted' }}
                  opacity={1}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef;
                    return (
                      <Td
                        key={cell.id}
                        w={meta?.size}
                        minW={meta?.minSize}
                        fontSize="13px"
                        fontWeight="400"
                        lineHeight="20px"
                        boxSizing="content-box"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
          </Tbody>
        </Show>
      </Table>
    </TableContainer>
  );
}
